import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AccessRequestSocketService } from '@service/access-request-socket.service';
import { AccessRequestService } from '@service/access-request.service';
import { AuthService } from '@service/auth.service';
import { NotificationsService } from '@service/homepage-notifications.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-request-access-message',
    templateUrl: './request-access-message.component.html',
    styleUrls: ['./request-access-message.component.css'],
    standalone: false,
})
export class RequestAccessMessageComponent implements OnInit, OnChanges, OnDestroy {
    @Input() vessel: Vessel;
    @Output() onBlurPage: EventEmitter<void> = new EventEmitter();

    shouldShowMessage: boolean = false;
    messageType: AccessRequestMessageType;
    accessRequest: AccessRequest;
    private destroySubscriptions$: Subject<void> = new Subject();

    currentUser: { name: string; oid: string; email: string };

    constructor(
        private accessRequestService: AccessRequestService,
        private accessRequestSocketService: AccessRequestSocketService,
        private authService: AuthService,
        private notificationService: NotificationsService
    ) {
        this.currentUser = this.authService.getCurrentUser();
    }

    async ngOnInit() {
        const accessRequestList = await this.accessRequestService.getRequests(this.vessel.id);
        if (!accessRequestList || accessRequestList.length === 0) return;

        this.processAccessRequests(accessRequestList);
        this.startSubscriptions();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.vessel) {
            this.resetMessageState();

            if (!this.vessel || !this.vessel.ownerOid) return;

            const accessRequestList = await this.accessRequestService.getRequests(this.vessel.id);
            if (!accessRequestList || accessRequestList.length === 0) return;

            this.processAccessRequests(accessRequestList);
        }
    }

    private processAccessRequests(requests: AccessRequest[]): void {
        const unreadRequest = requests.find(req => !req.messageRead);

        if (unreadRequest) {
            this.handleUnreadRequest(unreadRequest);
        } else {
            this.handleReadRequests(requests);
        }
    }

    private handleUnreadRequest(request: AccessRequest): void {
        const currentUser = this.authService.getCurrentUser();

        switch (request.requestStatus) {
            case AccessRequestStatus.Pending:
                this.checkForPendingRequests(request);
                break;

            case AccessRequestStatus.Removed:
                if (request.vesselId === this.vessel.id && currentUser.oid === request.userOid) {
                    this.accessRequest = request;
                    this.messageType = AccessRequestMessageType.ACCESS_REMOVED;
                    this.shouldShowMessage = true;
                }
                break;

            case AccessRequestStatus.Editing:
                if (request.vesselId === this.vessel.id && currentUser.oid === request.userOid) {
                    this.accessRequestService.setCurrentAccessRequestId(request.id);
                    this.resetMessageState();
                }
                break;
            case AccessRequestStatus.Accepted:
                if (request.vesselId === this.vessel.id && currentUser.oid === request.userOid) {
                    this.accessRequest = request;
                    this.messageType = AccessRequestMessageType.ACCESS_ACCEPTED;
                    this.onBlurPage.emit();
                    this.shouldShowMessage = true;
                }
                break;

            case AccessRequestStatus.Rejected:
                if (request.vesselId === this.vessel.id && currentUser.oid === request.userOid) {
                    this.accessRequest = request;
                    this.messageType = AccessRequestMessageType.ACCESS_REJECTED;
                    this.shouldShowMessage = true;
                }
        }
    }

    private handleReadRequests(requests: AccessRequest[]): void {
        for (const request of requests) {
            this.checkAcceptedRejectedRequests(request);
            if (this.shouldShowMessage) {
                break;
            }
        }
    }

    private resetMessageState(): void {
        this.messageType = null;
        this.shouldShowMessage = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    showPendingMessage(): boolean {
        return this.messageType === AccessRequestMessageType.ACCESS_PENDING;
    }

    showAcceptedMessage(): boolean {
        return this.messageType === AccessRequestMessageType.ACCESS_ACCEPTED;
    }

    showLockedMessage(): boolean {
        return this.vessel.projectStatus !== ProjectStatus.Editing;
    }

    showRejectedMessage(): boolean {
        return this.messageType === AccessRequestMessageType.ACCESS_REJECTED;
    }

    showRemovedMessage(): boolean {
        return this.messageType === AccessRequestMessageType.ACCESS_REMOVED;
    }

    isOwnerVessel(): boolean {
        return this.currentUser.oid === this.vessel.ownerOid;
    }

    async acceptRequest() {
        try {
            await this.accessRequestService.acceptRequest(this.accessRequest.id);
            this.messageType = null;
            this.shouldShowMessage = false;
        } catch (err) {
            console.error(err);
        }
    }

    async rejectRequest() {
        try {
            await this.accessRequestService.rejectRequest(this.accessRequest.id);
            this.messageType = null;
            this.shouldShowMessage = false;
        } catch (err) {
            console.error(err);
        }
    }

    async startEditing() {
        try {
            this.messageType = null;
            this.shouldShowMessage = false;
            await this.accessRequestService.startEdit(this.accessRequest.id);
            this.accessRequestService.setCurrentAccessRequestId(this.accessRequest.id);
        } catch (err) {
            console.error(err);
        }
    }

    async closeRemovedMessage() {
        try {
            await this.accessRequestService.finishEdit(this.accessRequest.id);
            this.messageType = null;
            this.shouldShowMessage = false;
            this.accessRequest = null;
        } catch (err) {
            console.error(err);
        }
    }

    async closeRejectedMessage() {
        try {
            await this.notificationService.updateAccessRequest(this.accessRequest.id, true, true);
            this.messageType = null;
            this.shouldShowMessage = false;
            this.accessRequest = null;
        } catch (err) {
            console.error(err);
        }
    }

    private checkForPendingRequests(accessRequest: AccessRequest) {
        if (this.vessel.id === accessRequest.vesselId) {
            const currentUser = this.authService.getCurrentUser();
            if (currentUser.oid === this.vessel.ownerOid) {
                this.accessRequest = accessRequest;
                this.messageType = AccessRequestMessageType.ACCESS_PENDING;
                this.shouldShowMessage = true;
            }
        }
    }

    private checkAcceptedRejectedRequests(accessRequest: AccessRequest) {
        this.messageType = null;
        this.shouldShowMessage = false;
        const currentUser = this.authService.getCurrentUser();
        if (this.vessel.id === accessRequest.vesselId && currentUser.oid === accessRequest.userOid) {
            if (accessRequest.requestStatus === AccessRequestStatus.Accepted) {
                this.accessRequest = accessRequest;
                this.messageType = AccessRequestMessageType.ACCESS_ACCEPTED;
                this.onBlurPage.emit();
                this.shouldShowMessage = true;
            } else if (
                accessRequest.requestStatus === AccessRequestStatus.Rejected &&
                (accessRequest as any).messageRead == false
            ) {
                this.accessRequest = accessRequest;
                this.messageType = AccessRequestMessageType.ACCESS_REJECTED;
                this.shouldShowMessage = true;
            }
        }
    }

    private startSubscriptions() {
        this.accessRequestSocketService
            .requestCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((accessRequest: AccessRequest) => {
                this.checkForPendingRequests(accessRequest);
            });

        this.accessRequestSocketService
            .requestUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((accessRequest: AccessRequest) => {
                this.checkAcceptedRejectedRequests(accessRequest);
            });

        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((accessRequest: AccessRequest) => {
                if (accessRequest.vesselId !== this.vessel.id) return;
                const currentUser = this.authService.getCurrentUser();
                if (accessRequest.userOid == currentUser.oid) {
                    this.accessRequestService.setCurrentAccessRequestId(accessRequest.id);
                    this.messageType = null;
                    this.shouldShowMessage = false;
                }
            });

        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe((accessRequest: AccessRequest) => {
                if (
                    accessRequest.requestStatus === AccessRequestStatus.Removed &&
                    accessRequest.vesselId === this.vessel.id
                ) {
                    const currentUser = this.authService.getCurrentUser();
                    if (currentUser.oid === accessRequest.userOid) {
                        this.accessRequest = accessRequest;
                        this.messageType = AccessRequestMessageType.ACCESS_REMOVED;
                        this.shouldShowMessage = true;
                    }
                    const currentRequestId = this.accessRequestService.getCurrentAccessRequestId();
                    if (currentRequestId === accessRequest.id) {
                        this.accessRequestService.setCurrentAccessRequestId(undefined);
                    }
                }
            });
    }
}

export enum AccessRequestMessageType {
    ACCESS_PENDING = 'pending-access',
    ACCESS_REMOVED = 'removed-access',
    ACCESS_ACCEPTED = 'access-accepted',
    ACCESS_REJECTED = 'access-rejected',
}
